import { signedApiClient } from './apiClient';
import { UserModel, type IUserPermissions, type NetworkUser, type NetworkStudyUsers } from '@/models/user';
import type { FormModel } from '@/models/form';

/**
 * Page size used to pull the full member list in one request. The endpoint is
 * paginated with no "all" mode, so this is a ceiling rather than a page — high
 * enough for any real study, and `getAllStudyUsers` reports when a study exceeds
 * it instead of quietly returning a truncated list.
 */
const ALL_USERS_LIMIT = 500;

class StudyUsersService {

  private investigationEndpoint: string = '';
  private studyEndpoint: string = '';

  initService(
    investigationEndpoint: string,
    studyEndpoint: string
  ) {
    this.investigationEndpoint = investigationEndpoint;
    this.studyEndpoint = studyEndpoint;
  }

  /**
   * Every activated member of the study, for pickers that need the whole list.
   *
   * `getCurrentStudyUsers` cannot serve this: it is the backing query for the
   * paginated User Management table, so it returns one page (10 by default) and
   * searches server-side. A picker fed from it would silently offer only the
   * page the table happens to be showing.
   *
   * Permissions and form access are skipped — a picker needs a name, an email and
   * an avatar, and parsing the rest costs a `formStore` load this has no reason
   * to wait on.
   */
  async getAllStudyUsers(
    identifier: string
  ): Promise<{ users: UserModel[]; total: number; truncated: boolean }> {
    const uri = `${this.studyEndpoint}/${identifier}/paged-users?page=1&limit=${ALL_USERS_LIMIT}`;
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    const networkResponse: NetworkStudyUsers = endpointResponse.data;

    const users = (networkResponse.users ?? []).map(user => {
      user.status = 'Activated';
      return UserModel.mapFromBackend(user, '', []);
    });

    const total = networkResponse.usersCount ?? users.length;

    return { users, total, truncated: total > users.length };
  }

  async getCurrentStudyUsers(
    identifier: string,
    forms: FormModel[],
    page: number,
    limit: number,
    search?: string
  ): Promise<{ users: UserModel[]; usersCount: number }> {
    let uri = `${this.studyEndpoint}/${identifier}/paged-users?page=${page}&limit=${limit}`;
    if (search != null && search != '') {
      uri += `&search=${encodeURIComponent(search)}`;
    }
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    const networkResponse: NetworkStudyUsers = endpointResponse.data;

    const users = networkResponse.users.map(user => {
      user.status = 'Activated';
      this.parseFormsPermissions(user, forms);
      user.permissionsList = this.parsePermissions(user.permissions as IUserPermissions);
      return UserModel.mapFromBackend(user, '', forms);
    });
    return { users, usersCount: networkResponse.usersCount };
  }

  async getPendingStudyUsers(identifier: string, forms: FormModel[]): Promise<UserModel[]> {
    const uri = `${this.studyEndpoint}/${identifier}/pending-users`;
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    const networkPendingUsers: NetworkUser[] = endpointResponse.data;

    return networkPendingUsers
      .map(user => {
        user.status = 'Pending';
        user.identifier = user.inviteIdentifier;
        this.parseFormsPermissions(user, forms);
        user.permissionsList = this.parsePermissions(user.permissions as IUserPermissions);
        return UserModel.mapFromBackend(user, '', forms);
      })
      // newest invites first
      .sort((a, b) => new Date(b.expiresAt ?? 0).getTime() - new Date(a.expiresAt ?? 0).getTime());
  }

  parsePermissions(userPermissions: IUserPermissions) {
    const permissions: string[] = [];
    Object.entries(userPermissions).forEach(([key, value]) => {
      const typedKey = key as keyof IUserPermissions;
      if (typedKey === 'forms') {
        if (Array.isArray(value)) {
          permissions.push(`View forms: ${value.length} assigned`);
        } else {
          permissions.push('View forms: All assigned');
        }
      } else if (typedKey === 'editForms') {
        if (Array.isArray(value)) {
          permissions.push(`Add entries: ${value.length} assigned`);
        } else {
          permissions.push('Add entries: All assigned');
        }
      } else if (typedKey === 'viewEntries') {
        if (value) {
          permissions.push('View entries: All entries');
        } else {
          permissions.push('View entries: Own entries');
        }
      } else if (typedKey === 'export') {
        if (value) {
          permissions.push('Export data is allowed');
        } else {
          permissions.push('Export data is forbidden');
        }
      } else if (typedKey === 'manageInvest') {
        if (value) {
          permissions.push('Manage study is allowed');
        } else {
          permissions.push('Manage study is forbidden');
        }
      } else if (typedKey === 'createStudy') {
        if (value) {
          permissions.push('Create study is allowed');
        } else {
          permissions.push('Create study is forbidden');
        }
      } else if (typedKey === 'editCases') {
        if (value) {
          permissions.push('Edit cases is allowed');
        } else {
          permissions.push('Edit cases is forbidden');
        }
      }
    });
    return permissions;
  }

  parseFormsPermissions(user: NetworkUser, forms: FormModel[]) {
    user.formsPermissions =
    (user.permissions as IUserPermissions).forms === true ?
      forms.map(form => form.base64Id as string) :
      (user.permissions as IUserPermissions).forms as string[];
    user.editFormsPermissions =
    (user.permissions as IUserPermissions).editForms === true ?
      forms.map(form => form.base64Id as string) :
      (user.permissions as IUserPermissions).editForms as string[];
  }

  parseFormsPermissionsForUser(user: UserModel, forms: FormModel[]) {
    user.formsPermissions =
    (user.permissions as IUserPermissions).forms === true ?
      forms.map(form => form.base64Id as string) :
      (user.permissions as IUserPermissions).forms as string[];
    user.editFormsPermissions =
    (user.permissions as IUserPermissions).editForms === true ?
      forms.map(form => form.base64Id as string) :
      (user.permissions as IUserPermissions).editForms as string[];
  }

  async removeUserFromStudy(identifier: string, userIdentifier: string) {
    const uri = `${this.studyEndpoint}/${identifier}/user/${userIdentifier}`;
    const apiClient = signedApiClient('DELETE', uri);
    return await apiClient.delete(uri);
  }

  async inviteUserToStudy(studyId: string, invites: UserModel[], groupId?: string) {
    const uri = `${this.investigationEndpoint}/${studyId}/invites`;
    const apiClient = signedApiClient('POST', uri);

    const payloadInvites: unknown[] = [];
    invites.forEach(invite => {
      const groupIdentifier = invite.permissions.groupIdentifiers?.[0] ?? groupId;
      payloadInvites.push({
        email: invite.email.toLowerCase(),
        groupIdentifier: groupIdentifier,
        viewPermissions: invite.permissions.viewEntries ? groupIdentifier ? 'groupEntries' : 'allEntries' : 'ownEntries',
        editCases: invite.permissions.editCases,
        exportPermissions: invite.permissions.export,
        manageInvestPermissions: invite.permissions.manageInvest,
        forms: invite.permissions.forms,
        editForms: invite.permissions.editForms,
        createStudy: invite.permissions.createStudy,
        notificationProfile: invite.permissions.notificationProfile
      });
    });

    const response = await apiClient.post(uri, {
      invites: payloadInvites
    });
    return response.data;
  }

  async patchInvitePermissions(studyId: string, inviteIdentifier: string, permissions: IUserPermissions) {
    const uri = `${this.investigationEndpoint}/${studyId}/invite/${inviteIdentifier}`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, permissions);
  }
}

export default new StudyUsersService();
